import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  createUser,
  updateUser,
} from "../../../redux/features/users/userSlice";

import { getOrganizations } from "../../../redux/features/organizations/organizationSlice";

import "./UserForm.css";

const ALL_ROLE_OPTIONS = [
  { label: "POWER Admin", value: "POWER_ADMIN" },
  { label: "Organisatie admin", value: "POWER_ORGANIZATION_ADMIN" },
  { label: "Interne coach", value: "POWER_INTERNAL_COACH" },
  { label: "Externe coach", value: "POWER_EXTERNAL_COACH" },
];

const initialState = {
  first_name: "",
  last_name: "",
  email: "",
  role: "POWER_EXTERNAL_COACH",
  organization: "",
  core_user_id: "",
};

const rolesThatNeedOrganization = [
  "POWER_ORGANIZATION_ADMIN",
  "POWER_EXTERNAL_COACH",
];

const rolesThatUseCore = ["POWER_ADMIN", "POWER_INTERNAL_COACH"];

export default function UserForm({
  mode = "create",
  initialData = null,
  onSuccess,
  onCancel,
}) {
  const dispatch = useDispatch();

  const { isCreating, isUpdating, error } = useSelector((state) => state.users);
  const { user: loggedUser } = useSelector((state) => state.auth);
  const { organizations = [] } = useSelector((state) => state.organizations);

  const [formData, setFormData] = useState(initialState);
  const [validationErrors, setValidationErrors] = useState({});

  const isEditMode = mode === "edit";
  const isSubmitting = isCreating || isUpdating;

  const isPowerAdmin = loggedUser?.role === "POWER_ADMIN";
  const isOrganizationAdmin = loggedUser?.role === "POWER_ORGANIZATION_ADMIN";

  const availableRoles = useMemo(() => {
    if (isOrganizationAdmin) {
      return ALL_ROLE_OPTIONS.filter((role) =>
        ["POWER_INTERNAL_COACH", "POWER_EXTERNAL_COACH"].includes(role.value),
      );
    }

    return ALL_ROLE_OPTIONS;
  }, [isOrganizationAdmin]);

  const needsOrganization = rolesThatNeedOrganization.includes(formData.role);

  const isCoreUser = rolesThatUseCore.includes(formData.role);

  useEffect(() => {
    if (isPowerAdmin && !organizations.length) {
      dispatch(
        getOrganizations({
          page: 1,
          limit: 100,
          is_active: "true",
        }),
      );
    }
  }, [dispatch, isPowerAdmin, organizations.length]);

  useEffect(() => {
    if (isEditMode && initialData) {
      setFormData({
        first_name: initialData.first_name || "",
        last_name: initialData.last_name || "",
        email: initialData.email || "",
        role: initialData.role || "POWER_EXTERNAL_COACH",
        organization:
          initialData.organization?._id || initialData.organization || "",
        core_user_id: initialData.core_user_id || "",
      });

      return;
    }

    setFormData({
      ...initialState,
      role: isOrganizationAdmin
        ? "POWER_EXTERNAL_COACH"
        : "POWER_EXTERNAL_COACH",
    });
  }, [isEditMode, initialData, isOrganizationAdmin]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      const nextState = {
        ...prev,
        [name]: value,
      };

      if (name === "role" && !rolesThatNeedOrganization.includes(value)) {
        nextState.organization = "";
      }

      return nextState;
    });

    setValidationErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = "Voornaam is verplicht.";
    }

    if (!formData.last_name.trim()) {
      errors.last_name = "Achternaam is verplicht.";
    }

    if (!isEditMode && !formData.email.trim()) {
      errors.email = "E-mailadres is verplicht.";
    }

    if (
      !isEditMode &&
      formData.email &&
      !/^\S+@\S+\.\S+$/.test(formData.email)
    ) {
      errors.email = "Ongeldig e-mailadres.";
    }

    if (!isEditMode && !formData.role) {
      errors.role = "Rol is verplicht.";
    }

    if (isPowerAdmin && needsOrganization && !formData.organization) {
      errors.organization = "Organisatie is verplicht.";
    }

    if (!isEditMode && isCoreUser && !formData.core_user_id.trim()) {
      errors.core_user_id = "Core user id is verplicht.";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => {
    const payload = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
    };

    if (isEditMode) {
      if (isPowerAdmin) {
        payload.organization = formData.organization || null;
      }

      return payload;
    }

    payload.email = formData.email.trim().toLowerCase();
    payload.role = formData.role;

    if (isPowerAdmin && formData.organization) {
      payload.organization = formData.organization;
    }

    if (isCoreUser) {
      payload.core_user_id = formData.core_user_id.trim();
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      if (isEditMode) {
        await dispatch(
          updateUser({
            id: initialData._id,
            data: buildPayload(),
          }),
        ).unwrap();
      } else {
        await dispatch(createUser(buildPayload())).unwrap();
      }

      onSuccess?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form className="user-form" onSubmit={handleSubmit}>
      {error && <div className="user-form__error">{error}</div>}

      <div className="user-form__grid">
        <div className="user-form__group">
          <label>Voornaam *</label>

          <input
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="Voornaam"
            className={
              validationErrors.first_name ? "user-form__input--error" : ""
            }
          />

          {validationErrors.first_name && (
            <span>{validationErrors.first_name}</span>
          )}
        </div>

        <div className="user-form__group">
          <label>Achternaam *</label>

          <input
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="Achternaam"
            className={
              validationErrors.last_name ? "user-form__input--error" : ""
            }
          />

          {validationErrors.last_name && (
            <span>{validationErrors.last_name}</span>
          )}
        </div>

        <div className="user-form__group user-form__group--full">
          <label>E-mailadres {!isEditMode && "*"}</label>

          <input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            disabled={isSubmitting || isEditMode}
            placeholder="naam@topuntgent.be"
            className={validationErrors.email ? "user-form__input--error" : ""}
          />

          {validationErrors.email && <span>{validationErrors.email}</span>}
        </div>

        {!isEditMode && (
          <div className="user-form__group">
            <label>Rol *</label>

            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={isSubmitting}
              className={validationErrors.role ? "user-form__input--error" : ""}
            >
              {availableRoles.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>

            {validationErrors.role && <span>{validationErrors.role}</span>}
          </div>
        )}

        {isPowerAdmin && (needsOrganization || isEditMode) && (
          <div className="user-form__group">
            <label>
              Organisatie {needsOrganization && !isEditMode ? "*" : ""}
            </label>

            <select
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              disabled={isSubmitting}
              className={
                validationErrors.organization ? "user-form__input--error" : ""
              }
            >
              <option value="">Geen organisatie</option>

              {organizations.map((organization) => (
                <option key={organization._id} value={organization._id}>
                  {organization.name} ({organization.code})
                </option>
              ))}
            </select>

            {validationErrors.organization && (
              <span>{validationErrors.organization}</span>
            )}
          </div>
        )}

        {!isEditMode && isCoreUser && (
          <div className="user-form__group">
            <label>Core user id *</label>

            <input
              name="core_user_id"
              value={formData.core_user_id}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="Core user id"
              className={
                validationErrors.core_user_id ? "user-form__input--error" : ""
              }
            />

            {validationErrors.core_user_id && (
              <span>{validationErrors.core_user_id}</span>
            )}
          </div>
        )}
      </div>

      {!isEditMode && isOrganizationAdmin && (
        <div className="user-form__info">
          Deze gebruiker wordt automatisch gekoppeld aan jouw organisatie.
        </div>
      )}

      {!isEditMode && isCoreUser && (
        <div className="user-form__info">
          Interne gebruikers en platform admins worden beheerd via het centrale
          TOPunt-systeem.
        </div>
      )}

      <div className="user-form__actions">
        <button
          type="button"
          className="user-form__cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Annuleren
        </button>

        <button
          type="submit"
          className="user-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? "Opslaan..."
            : isEditMode
              ? "Wijzigingen opslaan"
              : "Gebruiker aanmaken"}
        </button>
      </div>
    </form>
  );
}
